import BigNumber from 'bignumber.js';
import { phone } from 'phone';
import { AnyObject, MixedSchema, NumberSchema, StringSchema } from 'yup';

import { IFileWithImageSource } from '@webtrader/core/assets';

import i18n from '../i18n';

export const isEmptyObject = (obj: Record<string, unknown>) => JSON.stringify(obj) === '{}';

export const hasHttpProtocol = (val: string) => /https?:\/\//.test(val);

export const validateDigitsOnly = (number: string) => {
  const onlyDigitsRegex = /^\d*\.?\d*$/;

  return onlyDigitsRegex.test(number);
};

export const isEmpty = (value: string | number | undefined) => {
  if (typeof value === 'string') {
    return value === '';
  }

  return value === undefined || value === null;
};

export const hasWhitespace = (value: string) => {
  return /\s/g.test(value);
};

export const isAlphaNumeric = (value: string) => {
  return /^(?=.*[a-zA-Z])(?=.*[0-9])/.test(value);
};

export const containsOnlyNumbers = (value: string) => {
  return /^[0-9]+$/.test(value);
};

export const isValidEmail = (value: string) => {
  const emailAddRegex = /^[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/;

  return emailAddRegex.test(value);
};

export const validatePassword = (value: string) => {
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).{5,20}$/;

  return passwordRegex.test(value);
};

export const isValidOtp = (value: string) => {
  const otpRegex = /^[0-9]{6}$/;

  return otpRegex.test(value);
};

export const validatePasswordForMetaTraderPlatform = (value: string) => {
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[-!#$%&'"*+@^()_=+;:,.`~?<>/{}\[\]]).{12,20}$/;

  return passwordRegex.test(value);
};

export const validatePhoneNumber = (prefix: string, value: string) => {
  // This is special handle for SG number
  if (prefix === '+65' && value.length > 8) {
    return false;
  }

  const fullNumber = prefix + value;
  const validationResults = phone(fullNumber);

  return prefix === validationResults.countryCode && validationResults.isValid;
};

export const validateName = (name: string) => {
  const nameRegex = /[a-zA-Z]+/;

  return nameRegex.test(name);
};

export const validateUsername = (userName: string) => {
  const usernameRegex = /^\S+$/;

  return usernameRegex.test(userName);
};

export const isValidHexColor = (hexCode?: string) => {
  if (!hexCode || hexCode === '') {
    return false;
  }

  return /^#?([0-9A-F]{3}){1,2}$/i.test(hexCode);
};

export const isPropInvalid = (prop: unknown) => {
  return (
    typeof prop === 'undefined' ||
    prop === null ||
    (typeof prop === 'string' && prop.toLowerCase() === 'null') ||
    (typeof prop === 'object' && prop && Object.entries(prop).length === 0 && prop.constructor === Object)
  );
};

export const validateDecimalString = (schema: StringSchema<string | null | undefined>) => {
  return schema
    .matches(/^\d*(?:\.\d+)?$/, ({ label }) =>
      label ? i18n.t('{{label}} is invalid', { label }) : i18n.t('Field is invalid')
    )
    .matches(/^\d*(?:\.\d{1,2})?$/, () => i18n.t('Only a maximum of 2 d.p. allowed'));
};

export const validateLogoType = (schema: MixedSchema<IFileWithImageSource | null | undefined>) => {
  return schema.test('file-format', 'Invalid file format', (value) => {
    if (value) {
      return ['image/jpeg', 'image/png', 'image/gif'].includes(value.file.type);
    }
    return true;
  });
};

export const getRequiredText = ({ label }: { label: string }) =>
  label ? i18n.t('{{label}} is required', { label }) : i18n.t('Field is required');

export const getInvalidText = ({ label }: { label: string }) =>
  label ? i18n.t('{{label}} is invalid', { label }) : i18n.t('Field value is invalid');

export const referenceNumberRequiredMinCheck = (
  [refValue]: (number | null | undefined)[],
  schema: NumberSchema<number | null | undefined, AnyObject, undefined, ''>
) => {
  if (refValue === null || refValue === undefined) {
    return schema;
  }
  return schema.required(getRequiredText).min(refValue, ({ min }) => i18n.t('Value must be at least {{min}}', { min }));
};

export const referenceNumberRequiredMaxCheck = (
  [refValue]: (number | null | undefined)[],
  schema: NumberSchema<number | null | undefined, AnyObject, undefined, ''>
) => {
  if (refValue === null || refValue === undefined) {
    return schema;
  }
  return schema.required(getRequiredText).max(refValue, ({ max }) => i18n.t('Value must be at most {{max}}', { max }));
};

export const referenceNumberRequiredCheck = (
  [refValue]: (number | null | undefined)[],
  schema: NumberSchema<number | null | undefined, AnyObject, undefined, ''>
) => {
  if (refValue === null || refValue === undefined) {
    return schema;
  }
  return schema.required(getRequiredText);
};

export const transformInvalidNumberToNull = (currentValue: number | string | null | undefined) =>
  currentValue === '' || Number.isNaN(currentValue) ? null : currentValue;

export const validNumberStringTest = (value?: string) => {
  return value === null || value === undefined || value === '' || new BigNumber(value).isFinite();
};
